import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { PnsMacMini, PnsMacMiniStatus } from 'interfaces/pns-mac-mini'
import toast from 'react-hot-toast'

export interface CreateMacMiniParams {
  terminalId: string
  model: string
  serialNumber: string
  macAddress: string
  ipAddress?: string
  count?: number
  names: string[] // Generated names to use
}

interface CreateMacMiniRequest {
  name: string
  model: string
  serialNumber: string
  macAddress: string
  ipAddress?: string
  terminalId: string
  status: PnsMacMiniStatus
  ansibleId?: string
}

const createSingleMacMini = async (
  macMiniData: CreateMacMiniRequest
): Promise<PnsMacMini> => {
  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/mac_minis`,
    {
      method: API_METHODS.POST,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(macMiniData),
    }
  )
  response = handleErrors(response)
  return response.json()
}

const createMacMinis = async (
  params: CreateMacMiniParams
): Promise<PnsMacMini[]> => {
  const { names, terminalId, model, serialNumber, macAddress, ipAddress } =
    params

  // Create single request (count is always 1)
  const request: CreateMacMiniRequest = {
    name: names[0], // Use first (and only) name
    model,
    serialNumber, // Use exact serial number as entered
    macAddress, // Use exact MAC address as entered
    ipAddress: ipAddress,
    terminalId, // Include terminal ID for direct relationship
    status: PnsMacMiniStatus.RECEIVED,
    ansibleId: undefined, // RECEIVED devices are not assigned to ansible
  }

  // Execute single request
  const result = await createSingleMacMini(request)
  return [result] // Return array for consistency with existing interface
}

const useCreateMacMini = () => {
  const queryClient = useQueryClient()

  return useMutation(createMacMinis, {
    onSuccess: (data) => {
      const count = data.length
      toast.success(
        `Successfully created ${count} Mac Mini${count > 1 ? 's' : ''}`
      )

      // Invalidate related queries
      queryClient.invalidateQueries(['terminals'])
      queryClient.invalidateQueries(['received-mac-minis'])
      queryClient.invalidateQueries(['received-mac-minis-count'])
      queryClient.invalidateQueries(['terminal-detail'])
      queryClient.invalidateQueries(['next-device-names'])
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

export default useCreateMacMini
